import { ImportField } from "./types";

type RawFormField = { name?: unknown; label?: unknown };

type FormConfig = { sections?: Array<{ fields?: unknown }> };

export type BuildImportFieldsOptions = {
    /** Atributos que no se ofrecen como destino de una columna. */
    excluded?: Set<string>;
    /** Etiquetas propias donde la del formulario no describe lo que se va a importar. */
    labelOverrides?: Record<string, string>;
    /** Como normalizar cada atributo. Los que falten se tratan como texto. */
    kinds?: Record<string, string>;
    /** Atributos del modelo que no existen en el formulario y tambien se pueden mapear. */
    extraFields?: ImportField[];
};

/**
 * Recorre `sections[].fields[][]` de una configuracion de dynamic-forms. Los campos se
 * anidan en filas, y aqui solo interesa el par nombre/etiqueta.
 */
const flattenFormFields = (formConfig: FormConfig): RawFormField[] => {
    const sections = (formConfig?.sections || []) as Array<{ fields?: unknown }>;

    return sections.flatMap((section) => {
        const rows = (section?.fields || []) as unknown[];
        return rows.flatMap((row) => (Array.isArray(row) ? (row as RawFormField[]) : [row as RawFormField]));
    });
};

/**
 * Atributos mapeables de un modelo, con la etiqueta que ya usa su formulario de edicion.
 *
 * Leerlas del formulario en vez de repetirlas evita que el importador y la pantalla de
 * edicion se desincronicen cuando se renombra un campo.
 */
export const buildImportFieldsFromForm = (
    formConfig: FormConfig,
    options: BuildImportFieldsOptions = {}
): ImportField[] => {
    const { excluded, labelOverrides = {}, kinds = {}, extraFields = [] } = options;

    const fromForm = flattenFormFields(formConfig)
        .filter((field): field is { name: string; label?: unknown } => typeof field?.name === "string")
        .filter((field) => !excluded?.has(field.name))
        .map((field) => ({
            name: field.name,
            label: labelOverrides[field.name] || `${field.label ?? field.name}`,
            kind: kinds[field.name] || "text",
        }));

    const seen = new Set(fromForm.map((field) => field.name));

    return [...fromForm, ...extraFields.filter((field) => !seen.has(field.name))];
};
